// Installation parties admin page - companies authorised by an organization to
// physically open power cabinets. Renders one card per row, supports add /
// edit / delete via a single modal, and scopes by organization: organization-role
// users always see/manage their own organization's parties; platform-role
// users get an organization picker.
import { api } from '../api';
import { initAdminShell, escapeHtml, showError, hideError, confirmModal, setButtonBusy } from './shell';
import { t } from '../i18n/i18n';

interface Party {
  id: number;
  name: string;
  contactEmail: string | null;
  contactPhone: string | null;
  notificationEmails: string | null;
  organizationId: string;
  notes: string | null;
  createdAt: string;
}

interface Organization {
  id: string;
  name: string;
}

(async () => {
  const user = await initAdminShell('installation-parties').catch(() => null);
  if (!user) return;

  const isOrganization = user.role === 'organization';
  const list = document.getElementById('installer-list');
  if (!list) return;

  // Organization picker is only meaningful for platform users; organizations always
  // create against their own organization. Loaded once at startup.
  let organizations: Organization[] = [];
  if (!isOrganization) {
    try {
      const client = await api();
      const { data } = await client.get<Organization[]>('/api/admin/organizations');
      organizations = data;
    } catch {
      // Endpoint may 403 for some role configs; the picker stays empty and
      // creates fall back to the organization's own org if available.
    }
  }

  // ---- Modal state ----
  const overlay     = document.getElementById('inst-modal-overlay') as HTMLElement | null;
  const modalTitle  = document.getElementById('inst-modal-title') as HTMLElement | null;
  const form        = document.getElementById('inst-form') as HTMLFormElement | null;
  const errorBox    = document.getElementById('inst-modal-error') as HTMLElement | null;
  const closeBtn    = document.getElementById('inst-modal-close');
  const cancelBtn   = document.getElementById('inst-modal-cancel');
  const submitBtn   = document.getElementById('inst-modal-submit') as HTMLButtonElement | null;
  const nameInput   = document.getElementById('inst-name') as HTMLInputElement | null;
  const emailInput  = document.getElementById('inst-email') as HTMLInputElement | null;
  const phoneInput  = document.getElementById('inst-phone') as HTMLInputElement | null;
  const notifyInput = document.getElementById('inst-notify-input') as HTMLInputElement | null;
  const notifyChips = document.getElementById('inst-notify-chips') as HTMLDivElement | null;
  const notifyAddBtn = document.getElementById('inst-notify-add') as HTMLButtonElement | null;
  const notesInput  = document.getElementById('inst-notes') as HTMLTextAreaElement | null;

  // Notification-address chips: type an address, Add (or Enter) → chip; each
  // chip removable. Serialised to a comma-separated string on submit (the
  // backend stores/parses NotificationEmails that way).
  let notifyEmails: string[] = [];

  function renderNotifyChips(): void {
    if (!notifyChips) return;
    // Only reserve the gap below the chips when there actually are any.
    notifyChips.style.marginBottom = notifyEmails.length ? '8px' : '0';
    notifyChips.innerHTML = notifyEmails.map((email, i) => `
      <span style="display:inline-flex;align-items:center;gap:6px;background:var(--sk-blue-100,#eff3fb);border:1px solid var(--sk-border,#d6dce6);border-radius:20px;padding:4px 8px 4px 12px;font-size:12.5px;color:var(--sk-navy-800,#0a2a5c);">
        <span>${escapeHtml(email)}</span>
        <button type="button" data-remove-idx="${i}" aria-label="${escapeHtml(t('admin.actions.delete'))}" style="background:transparent;border:0;cursor:pointer;color:var(--sk-muted);font-size:15px;line-height:1;padding:0 2px;">×</button>
      </span>`).join('');
    notifyChips.querySelectorAll<HTMLButtonElement>('button[data-remove-idx]').forEach((btn) => {
      btn.addEventListener('click', () => {
        notifyEmails.splice(Number(btn.dataset.removeIdx), 1);
        renderNotifyChips();
      });
    });
  }

  function addNotifyEmail(): void {
    const raw = notifyInput?.value.trim() ?? '';
    if (!raw) return;
    if (!raw.includes('@')) { showModalError(t('admin.installation_parties.err_email_invalid')); return; }
    if (!notifyEmails.some((e) => e.toLowerCase() === raw.toLowerCase())) notifyEmails.push(raw);
    if (notifyInput) notifyInput.value = '';
    if (errorBox) errorBox.style.display = 'none';
    renderNotifyChips();
    notifyInput?.focus();
  }

  notifyAddBtn?.addEventListener('click', addNotifyEmail);
  notifyInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); addNotifyEmail(); }
  });

  // Inject an Organization select for platform users - the designer's modal
  // didn't include one because the demo only had a single organization.
  // Created once on first render and toggled hidden for organization users.
  let organizationSelect: HTMLSelectElement | null = null;
  if (form && !isOrganization && organizations.length > 0) {
    const wrap = document.createElement('div');
    wrap.className = 'form-field form-field--required';
    wrap.innerHTML = `
      <label for="inst-owner">${escapeHtml(t('admin.installation_parties.label_owner'))}</label>
      <select id="inst-owner" name="organizationId" required>
        ${organizations.map(o => `<option value="${escapeHtml(o.id)}">${escapeHtml(o.name)}</option>`).join('')}
      </select>
    `;
    form.insertBefore(wrap, form.firstChild);
    organizationSelect = wrap.querySelector('select');
  }

  let editingId: number | null = null;

  function openModal(party?: Party): void {
    if (!overlay || !form) return;
    editingId = party?.id ?? null;
    if (modalTitle) modalTitle.textContent = t(party ? 'admin.installation_parties.title_edit' : 'admin.installation_parties.title_new');
    if (nameInput)  nameInput.value  = party?.name         ?? '';
    if (emailInput) emailInput.value = party?.contactEmail ?? '';
    if (phoneInput) phoneInput.value = party?.contactPhone ?? '';
    notifyEmails = (party?.notificationEmails ?? '')
      .split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
    if (notifyInput) notifyInput.value = '';
    renderNotifyChips();
    if (notesInput) notesInput.value = party?.notes        ?? '';
    if (organizationSelect) {
      organizationSelect.value = party?.organizationId ?? organizations[0]?.id ?? '';
      organizationSelect.disabled = party != null; // organization is fixed once created
    }
    if (errorBox) { errorBox.style.display = 'none'; errorBox.textContent = ''; }
    overlay.classList.add('is-open');
    overlay.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
    nameInput?.focus();
  }

  function closeModal(): void {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    editingId = null;
  }

  function showModalError(msg: string): void {
    if (!errorBox) return;
    errorBox.textContent = msg;
    errorBox.style.display = 'block';
  }

  closeBtn?.addEventListener('click', closeModal);
  cancelBtn?.addEventListener('click', closeModal);
  overlay?.addEventListener('click', (e) => { if (e.target === overlay) closeModal(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && overlay?.classList.contains('is-open')) closeModal(); });

  // ---- Add button ----
  document.getElementById('add-installer-btn')?.addEventListener('click', () => openModal());

  // ---- Submit (create / update) ----
  submitBtn?.addEventListener('click', async () => {
    if (!form?.reportValidity()) return;

    const name  = nameInput?.value.trim() ?? '';
    const email = emailInput?.value.trim() || null;
    const phone = phoneInput?.value.trim() || null;
    // Fold in an address the user typed but didn't explicitly "Add".
    const leftover = notifyInput?.value.trim();
    if (leftover && leftover.includes('@') && !notifyEmails.some((e) => e.toLowerCase() === leftover.toLowerCase())) {
      notifyEmails.push(leftover);
    }
    const notificationEmails = notifyEmails.length ? notifyEmails.join(', ') : null;
    const notes = notesInput?.value.trim() || null;
    const organizationId = isOrganization
      ? (user.organizationId ?? '')
      : (organizationSelect?.value ?? organizations[0]?.id ?? '');

    if (!name) { showModalError(t('admin.installation_parties.err_name')); return; }
    if (!organizationId) { showModalError(t('admin.installation_parties.err_owner')); return; }

    setButtonBusy(submitBtn, true);
    try {
      const client = await api();
      if (editingId == null) {
        await client.post('/api/admin/installation-parties', { name, contactEmail: email, contactPhone: phone, notificationEmails, organizationId, notes });
      } else {
        await client.put(`/api/admin/installation-parties/${editingId}`, { name, contactEmail: email, contactPhone: phone, notificationEmails, notes });
      }
      closeModal();
      await load();
    } catch (err) {
      showModalError(err instanceof Error ? err.message : t('admin.installation_parties.err_save'));
    } finally {
      setButtonBusy(submitBtn, false);
    }
  });

  // ---- Render ----

  function render(parties: Party[]): void {
    if (!list) return;
    if (parties.length === 0) {
      list.innerHTML = `<div class="installer-empty">${escapeHtml(t('admin.installation_parties.empty'))}</div>`;
      return;
    }
    const editLabel = t('admin.actions.edit');
    const deleteLabel = t('admin.actions.delete');
    const groupBy = !isOrganization && organizations.length > 1;
    const organizationName = (id: string) => organizations.find(o => o.id === id)?.name ?? id;

    if (groupBy) {
      // Group by organization so a platform user sees one section per municipality.
      const grouped = new Map<string, Party[]>();
      for (const p of parties) {
        const arr = grouped.get(p.organizationId) ?? [];
        arr.push(p);
        grouped.set(p.organizationId, arr);
      }
      list.innerHTML = Array.from(grouped.entries()).map(([oid, items]) => `
        <div class="admin-side__title" style="padding:14px 0 6px;font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:var(--sk-muted);">
          ${escapeHtml(organizationName(oid))}
        </div>
        ${items.map((p) => renderCard(p, editLabel, deleteLabel)).join('')}
      `).join('');
    } else {
      list.innerHTML = parties.map((p) => renderCard(p, editLabel, deleteLabel)).join('');
    }

    list.querySelectorAll<HTMLButtonElement>('button[data-action="edit"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = Number(btn.dataset.id);
        const party = parties.find((p) => p.id === id);
        if (party) openModal(party);
      });
    });
    list.querySelectorAll<HTMLButtonElement>('button[data-action="delete"]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = Number(btn.dataset.id);
        const label = btn.dataset.label ?? '';
        if (!(await confirmModal({
          title: t('admin.confirm.modal_title_delete'),
          message: t('admin.installation_parties.delete_confirm').replace('{label}', label),
          confirmLabel: t('admin.confirm.modal_confirm_delete'),
        }))) return;
        setButtonBusy(btn, true);
        try {
          const client = await api();
          await client.delete(`/api/admin/installation-parties/${id}`);
          await load();
        } catch (err) {
          showError('inst-error', err instanceof Error ? err.message : t('admin.installation_parties.err_delete'));
          setButtonBusy(btn, false);
        }
      });
    });
  }

  function renderCard(p: Party, editLabel: string, deleteLabel: string): string {
    const email = p.contactEmail
      ? `<a href="mailto:${escapeHtml(p.contactEmail)}">${escapeHtml(p.contactEmail)}</a>`
      : '';
    const phone = p.contactPhone
      ? `<a href="tel:${escapeHtml(p.contactPhone)}">${escapeHtml(p.contactPhone)}</a>`
      : '';
    const contactLine = [email, phone].filter(Boolean).join(' · ');
    const notify = p.notificationEmails
      ? `<div class="installer-card__contact" style="color:var(--sk-muted);font-size:12px;">${escapeHtml(t('admin.installation_parties.cc_prefix'))} ${escapeHtml(p.notificationEmails)}</div>`
      : '';
    return `
      <div class="installer-card" data-id="${p.id}">
        <div>
          <div class="installer-card__name">${escapeHtml(p.name)}</div>
          ${contactLine ? `<div class="installer-card__contact">${contactLine}</div>` : ''}
          ${notify}
          ${p.notes ? `<div class="installer-card__notes">${escapeHtml(p.notes)}</div>` : ''}
        </div>
        <div class="installer-card__actions">
          <button type="button" data-action="edit" data-id="${p.id}">${escapeHtml(editLabel)}</button>
          <button type="button" class="danger" data-action="delete" data-id="${p.id}" data-label="${escapeHtml(p.name)}">${escapeHtml(deleteLabel)}</button>
        </div>
      </div>
    `;
  }

  async function load(): Promise<void> {
    hideError('inst-error');
    try {
      const client = await api();
      const { data } = await client.get<Party[]>('/api/admin/installation-parties');
      render(data);
    } catch (err) {
      showError('inst-error', err instanceof Error ? err.message : t('admin.installation_parties.err_load'));
    }
  }

  await load();
})();
